import * as React from 'react'
import { animate, useMotionValue } from 'framer-motion'

function defaultFormat(value: number) {
  const rounded = Math.round(value)
  const safe = Number.isFinite(rounded) ? rounded : 0
  return Math.max(0, safe).toLocaleString()
}

export function useAnimatedNumber({
  value,
  duration = 1000,
  format,
}: {
  value: number
  duration?: number
  format?: (value: number) => string
}) {
  const formatterRef = React.useRef(format ?? defaultFormat)
  const motionValue = useMotionValue(value)
  const [displayValue, setDisplayValue] = React.useState(() => formatterRef.current(value))
  const isFirstRenderRef = React.useRef(true)

  React.useEffect(() => {
    formatterRef.current = format ?? defaultFormat
    setDisplayValue(formatterRef.current(motionValue.get()))
  }, [format, motionValue])

  React.useEffect(() => {
    const unsubscribe = motionValue.on('change', latest => {
      const safeValue = Number.isFinite(latest) ? latest : value
      setDisplayValue(formatterRef.current(safeValue))
    })

    return unsubscribe
  }, [motionValue, value])

  React.useEffect(() => {
    if (duration <= 0) {
      motionValue.set(value)
      setDisplayValue(formatterRef.current(value))
      isFirstRenderRef.current = false
      return
    }

    if (isFirstRenderRef.current) {
      motionValue.set(value)
      setDisplayValue(formatterRef.current(value))
      isFirstRenderRef.current = false
      return
    }

    const controls = animate(motionValue, value, {
      duration: duration / 1000,
      ease: 'easeOut',
    })

    return () => {
      controls.stop()
    }
  }, [duration, motionValue, value])

  return displayValue
}
